import type { RequestOptions, SearchRequest } from 'searchkit';

import type { SearchkitWrapperProps } from '@uncinc/react-searchkit/dist/components/Wrapper';

// We make use of the requestOptions hook `beforeSearch` to alter the
// search request body. By doing that we're able to filter the results
// on the existence of the `computed_first_upcoming_date_index` field.
// That will filter out "past" events that only have dates from before today.
export const requestOptions: RequestOptions = {
  hooks: {
    beforeSearch: (requests: SearchRequest[]) => new Promise((resolve) => {
      requests.forEach((request) => {
        // eslint-disable-next-line
        (request.body.query?.bool?.filter as Array<any>)?.push({
          exists: {
            field: 'computed_first_upcoming_date_index',
          },
        });
      });

      resolve(requests);
    }),
  },
  getBaseFilters: () => [
    {
      bool: {
        must: {
          term: {
            type: {
              value: 'event',
            },
          },
        },
      },
    },
  ],
};

export const sorting: SearchkitWrapperProps['searchSettings']['sorting'] = {
  default: {
    field: 'computed_first_upcoming_start_date',
    order: 'asc',
  },
};

export const runtimeMappings: SearchkitWrapperProps['searchSettings']['runtime_mappings'] = {
  // Define a runtime field that will loop over the `event_date` array
  // to find the first upcoming date in the future. Then we can use this field
  // to sort on that value.
  computed_first_upcoming_start_date: {
    type: 'date',
    script: {
      source: `
      def firstUpcomingDate = null;
      def hasAnUpcomingDate = false;
      def today = new Date().getTime();

      for (int i = 0; i < params._source.event_date.length; i++) {
        ZonedDateTime date = ZonedDateTime.parse(params._source.event_date[i] + 'Z');

        if (new Date(date.toInstant().toEpochMilli()).getTime() > today) {
          if (!hasAnUpcomingDate) {
            firstUpcomingDate = new Date(date.toInstant().toEpochMilli()).getTime();
            hasAnUpcomingDate = true;
          } else if (new Date(date.toInstant().toEpochMilli()).getTime() < firstUpcomingDate) {
            firstUpcomingDate = new Date(date.toInstant().toEpochMilli()).getTime();
          }
        }
      }

      if (firstUpcomingDate !== null) {
        emit(firstUpcomingDate)
      } else {
        return;
      }
    `,
    },
  },
  // This runtime field will grab the index of the first upcoming date from
  // the `event_date` array. We need that to grab the corresponding end date
  // from the `event_date_end_value`.
  computed_first_upcoming_date_index: {
    type: 'long',
    script: {
      source: `
      def firstUpcomingDate = null;
      def firstUpcomingDateIndex = null;
      def hasAnUpcomingDate = false;
      def today = new Date().getTime();

      for (int i = 0; i < params._source.event_date.length; i++) {
        ZonedDateTime date = ZonedDateTime.parse(params._source.event_date[i] + 'Z');

        if (new Date(date.toInstant().toEpochMilli()).getTime() > today) {
          if (!hasAnUpcomingDate) {
            firstUpcomingDate = new Date(date.toInstant().toEpochMilli()).getTime();
            firstUpcomingDateIndex = i;
            hasAnUpcomingDate = true;
          } else if (new Date(date.toInstant().toEpochMilli()).getTime() < firstUpcomingDate) {
            firstUpcomingDate = new Date(date.toInstant().toEpochMilli()).getTime();
            firstUpcomingDateIndex = i;
          }
        }
      }

      if (firstUpcomingDateIndex !== null) {
        emit(firstUpcomingDateIndex)
      } else {
        return;
      }
    `,
    },
  },
};
